import React, { useEffect } from "react";
import axios from "axios";
import "@/App.css";
import "@/styles/knowledgebase.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import RouteSEO from "@/components/RouteSEO";
import PreviewHighlighter from "@/components/cms/PreviewHighlighter";
import CmsAmbientCapture from "@/components/cms/CmsAmbientCapture";

// Send httpOnly session cookies with every request so the admin cookie
// (`xaluca_admin_session`) travels alongside axios calls. Fetch calls set
// `credentials: "include"` explicitly at the call site or via `adminFetch`.
axios.defaults.withCredentials = true;

import { LanguageProvider } from "@/contexts/LanguageContext";
import { CmsProvider } from "@/contexts/CmsContext";
import { BookingProvider } from "@/contexts/BookingContext";
import { CurrencyProvider } from "@/contexts/CurrencyContext";
import Layout from "@/components/Layout";
import StickySummary from "@/components/StickySummary";
import ClearAllConfirmDialog from "@/components/ClearAllConfirmDialog";

// Single source of truth for every URL in the app. Adding an entry to
// `routesRegistry.jsx` automatically creates the live route AND
// surfaces it in /admin/routes — no need to touch this file when a new
// page ships.
import ROUTES from "@/lib/routesRegistry";

const ScrollToTop = () => {
  const { pathname, search } = useLocation();
  useEffect(() => {
    // When admins deep-link from /admin/images with ?cmsPreview=<slot>,
    // PreviewHighlighter will scroll the target <img> into view — the
    // default top-jump would fight that and cause an obvious flicker,
    // so we skip the reset on those navigations.
    if (search && search.includes("cmsPreview=")) return;
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname, search]);
  return null;
};

function App() {
  return (
    <HelmetProvider>
    <LanguageProvider>
      <CurrencyProvider>
      <BrowserRouter>
        <ScrollToTop />
        <RouteSEO />
        <CmsProvider>
        <PreviewHighlighter />
        <CmsAmbientCapture />
        <BookingProvider>
        <Layout>
          <Routes>
            {ROUTES.map((r) => (
              <Route key={r.path} path={r.path} element={r.element} />
            ))}
          </Routes>
          <StickySummary />
          <ClearAllConfirmDialog />
        </Layout>
        </BookingProvider>
        </CmsProvider>
      </BrowserRouter>
      </CurrencyProvider>
    </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
